<script lang="ts">
    // Folha de orçamento (v4, mobile), no mesmo padrão da folha de venda e da de agendamento.
    // É só a apresentação: toda regra — itens de pacote, validade, faturamento, modelos, impressão e
    // compartilhamento — continua em quotation-form.svelte, que no mobile entrega o estado para cá.
    // Os editores de item e de opção de pagamento também são os de sempre (quotation-form-items e
    // quotation-form-payments): aqui só desenhamos a linha fechada e abrimos o editor ao tocar.
    import Fa from 'svelte-fa'
    import { faBoxOpen, faCreditCard, faPlus, faPrint, faShareAlt, faShoppingCart, faTrashAlt, faUser } from '@fortawesome/free-solid-svg-icons'
    import { slide } from 'svelte/transition'
    import type { QuotationEntity, ServiceOnQuotation, QuotationPayment } from '../../../../modules/posApi/quotation/quotation.entity'
    import Zsheet from '../../../_common/z/sheet/zsheet.svelte'
    import ZsheetSection from '../../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetEntity from '../../../_common/z/sheet/zsheet-entity.svelte'
    import Zinput from '../../../_common/z/zinput.svelte'
    import ZentitySelect from '../../../_common/z/zentity-select.svelte'
    import QuotationFormItems from '../quotation-form-items.svelte'
    import QuotationFormPayments from '../quotation-form-payments.svelte'

    // ---- estado (do quotation-form; com bind no que a folha altera) ----
    export let entity: QuotationEntity
    export let isModel: boolean = false
    export let newService: boolean = false
    export let newPayment: boolean = false
    export let show: boolean = false
    export let models: QuotationEntity[] = []
    export let modelId: string = null

    // ---- ações (funções do quotation-form: a regra mora lá) ----
    export let onSave: () => void
    export let onRemove: () => void
    export let onPrint: () => void
    export let onShare: () => void
    export let onInvoice: () => void
    export let onModelSelected: () => void
    export let onServiceAdded: (ev: any) => void
    export let onServiceClosed: () => void
    export let onPaymentAdded: (ev: any) => void
    export let onPaymentClosed: () => void
    export let onBack: () => void

    const canWrite = !!window.store.token.permissions.pos.quotes?.write
    const money = (value: number) => window.api.utils.utils.toReal(value || 0)

    $: items = (entity?.items || []).filter((el) => !!el)
    $: payments = (entity?.payments || []).filter((el) => !!el)
    $: total = items.reduce((acc, el) => acc + (el.value || 0) * (el.quantity || 0), 0)

    // ---- itens ----
    let editingItem: number = null

    function itemLabel(item: Partial<ServiceOnQuotation>) {
        const type = item?.service?.type
        if (item?.parentServiceId) return ''
        if (type === 'package') return 'Pacote'
        if (type === 'product') return 'Produto'
        if (type === 'consumable') return 'Insumo'
        return 'Serviço'
    }

    function itemSubtitle(item: Partial<ServiceOnQuotation>) {
        if (item.service?.type === 'package') return ''
        return `${item.quantity || 0} × ${money(item.value)}`
    }

    function openItem(index: number) {
        newService = false
        editingItem = index
    }

    // o editor fecha sozinho ao confirmar (✓) ou excluir (lixeira), como no desktop
    function itemFinished() {
        editingItem = null
        onServiceClosed()
    }

    // ---- opções de pagamento ----
    let editingPayment: number = null

    // mesmas frases do editor (quotation-form-payments): "R$ 100,00 no cartão de crédito em até 6 vezes"
    function prep(metodo: string) {
        if (metodo === 'Dinheiro') return 'em'
        if (metodo === 'Pix') return 'por'
        if (metodo === 'Financeira') return 'via'
        if (metodo === 'Outros*') return 'via'
        return 'no'
    }

    function friendlyInstallment(payment: Partial<QuotationPayment>) {
        if (payment.installmentFrom === payment.installmentTo) {
            return payment.installmentFrom === 1 ? 'em 1 vez' : 'em ' + payment.installmentFrom + ' vezes'
        }
        if (payment.installmentFrom === 1) return 'em até ' + payment.installmentTo + ' vezes'
        return 'de ' + payment.installmentFrom + ' a ' + payment.installmentTo + ' vezes'
    }

    function paymentTitle(payment: Partial<QuotationPayment>) {
        const metodo = (payment.installmentMethod || '').toLowerCase()
        return `${money(payment.installmentValue)} ${prep(payment.installmentMethod)} ${metodo} ${friendlyInstallment(payment)}`
    }

    function paymentSubtitle(payment: Partial<QuotationPayment>) {
        if (!payment.dpValue) return 'Sem entrada'
        return `Entrada de ${money(payment.dpValue)} ${prep(payment.dpMethod)} ${(payment.dpMethod || '').toLowerCase()}`
    }

    function openPayment(index: number) {
        newPayment = false
        editingPayment = index
    }

    function paymentFinished() {
        editingPayment = null
        onPaymentClosed()
    }

    // ---- cliente ----
    $: person = entity?.person
    $: personInitials = (person?.name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((el) => el[0])
        .join('')

    const validadeOptions = Array.from({ length: 45 }, (_, i) => ({ value: i + 1, label: `${i + 1} ${i === 0 ? 'dia' : 'dias'}` }))
    $: modelOptions = [{ value: '', label: '[não utilizar modelo]' }, ...(models || []).map((el) => ({ value: el.quotationId, label: el.title }))]
</script>

<Zsheet>
    {#if isModel}
        <!-- MODELO: o orçamento vira um molde reutilizável; não tem cliente nem faturamento -->
        <ZsheetSection label="Modelo">
            <Zinput type="text" label="Nome do modelo" bind:value={entity.title} />
        </ZsheetSection>
    {:else}
        <!-- CLIENTE -->
        <ZsheetSection label="Cliente" meta={person?.personId ? '' : 'obrigatório'}>
            <!-- personId, e não person: ao tocar em "+" para cadastrar, o seletor preenche a pessoa em branco
                 (ainda sem id) e abre o cadastro dentro dele -->
            {#if person?.personId}
                <ZsheetEntity
                    initials={personInitials}
                    icon={faUser}
                    title={person.name}
                    subtitle={person.phone ? window.api.utils.utils.phoneFriendly(person.phone) : ''}
                    removable={!entity.quotationId}
                    removeTitle="Trocar cliente"
                    on:remove={() => (entity.person = null)}
                />
            {:else}
                <ZentitySelect fonte="pessoa" label="Buscar por nome, telefone ou e-mail" bind:value={entity.person} />
            {/if}
        </ZsheetSection>

        {#if !entity.quotationId && models.length > 0}
            <ZsheetSection label="Partir de um modelo">
                <Zinput type="select" label="Modelo" options={modelOptions} bind:value={modelId} on:change={onModelSelected} />
            </ZsheetSection>
        {/if}
    {/if}

    <!-- ITENS -->
    <ZsheetSection label="Itens" meta={items.length ? money(total) : ''}>
        <div class="list">
            {#each entity.items || [] as item, index}
                {#if item}
                    <div transition:slide={{ duration: 160 }} class:child={!!item.parentServiceId}>
                        {#if editingItem === index}
                            <div class="edit">
                                <QuotationFormItems startEditing={true} bind:item={entity.items[index]} on:cancel={itemFinished} on:added={itemFinished} />
                            </div>
                        {:else}
                            <ZsheetEntity
                                icon={item.service?.type === 'package' ? faBoxOpen : null}
                                initials={item.parentServiceId || item.service?.type === 'package' ? '' : (item.service?.name || '?').substring(0, 1)}
                                title={item.service?.name || '[item não cadastrado]'}
                                subtitle={itemSubtitle(item)}
                                clickable={canWrite}
                                on:click={() => canWrite && openItem(index)}
                            >
                                {#if itemLabel(item)}<span class="tag">{itemLabel(item)}</span>{/if}
                                <svelte:fragment slot="trailing">
                                    {#if item.service?.type !== 'package'}
                                        <span class="amount">{money((item.value || 0) * (item.quantity || 0))}</span>
                                    {/if}
                                </svelte:fragment>
                            </ZsheetEntity>
                        {/if}
                    </div>
                {/if}
            {/each}

            {#if newService}
                <div class="edit" transition:slide={{ duration: 160 }}>
                    <QuotationFormItems on:cancel={onServiceClosed} on:added={onServiceAdded} />
                </div>
            {:else if canWrite}
                <button type="button" class="add" on:click={() => ((editingItem = null), (newService = true))}><Fa icon={faPlus} /> Item</button>
            {/if}
        </div>
    </ZsheetSection>

    <!-- OPÇÕES DE PAGAMENTO -->
    <ZsheetSection label="Opções de pagamento" meta={payments.length ? `${payments.length} ${payments.length === 1 ? 'opção' : 'opções'}` : ''}>
        <div class="list">
            {#each entity.payments || [] as payment, index}
                {#if payment}
                    <div transition:slide={{ duration: 160 }}>
                        {#if editingPayment === index}
                            <div class="edit">
                                <QuotationFormPayments
                                    startEditing={true}
                                    bind:item={entity.payments[index]}
                                    on:cancel={paymentFinished}
                                    on:added={paymentFinished}
                                />
                            </div>
                        {:else}
                            <ZsheetEntity
                                icon={faCreditCard}
                                title={paymentTitle(payment)}
                                subtitle={paymentSubtitle(payment)}
                                clickable={canWrite}
                                on:click={() => canWrite && openPayment(index)}
                            >
                                <svelte:fragment slot="trailing">
                                    <span class="amount">{money((payment.installmentValue || 0) + (payment.dpValue || 0))}</span>
                                </svelte:fragment>
                            </ZsheetEntity>
                        {/if}
                    </div>
                {/if}
            {/each}

            {#if newPayment}
                <div class="edit" transition:slide={{ duration: 160 }}>
                    <QuotationFormPayments on:cancel={onPaymentClosed} on:added={onPaymentAdded} />
                </div>
            {:else if canWrite}
                <button type="button" class="add" on:click={() => ((editingPayment = null), (newPayment = true))}><Fa icon={faPlus} /> Opção de pagamento</button>
            {/if}
        </div>
    </ZsheetSection>

    <!-- DETALHES -->
    <ZsheetSection label="Detalhes">
        <div class="details">
            <Zinput type="select" label="Validade da proposta" options={validadeOptions} bind:value={entity.expiresAt} />
            <Zinput type="textarea" rows={4} label="Observações" bind:value={entity.notes} />
        </div>
    </ZsheetSection>

    <!-- AÇÕES -->
    {#if canWrite && entity.quotationId && !isModel}
        <ZsheetSection label="Este orçamento">
            <div class="actions">
                <button type="button" class="add -solid" on:click={onPrint}><Fa icon={faPrint} /> Imprimir</button>
                <button type="button" class="add -solid" on:click={onShare}><Fa icon={faShareAlt} /> Compartilhar link</button>
                <button type="button" class="add -solid" on:click={onInvoice}>
                    <Fa icon={faShoppingCart} />
                    {entity.invoiceId ? 'Faturamento' : 'Faturar'}
                </button>
            </div>
        </ZsheetSection>
    {/if}

    <!-- EXCLUIR -->
    {#if canWrite && entity.quotationId}
        <ZsheetSection tone="danger">
            <button type="button" class="danger" disabled={show} on:click={onRemove}>
                <span class="danger_icon"><Fa icon={faTrashAlt} /></span>
                <span class="danger_text">
                    <span class="danger_title">{isModel ? 'Excluir modelo' : 'Excluir orçamento'}</span>
                    <span class="danger_hint">Apaga este registro.</span>
                </span>
            </button>
        </ZsheetSection>
    {/if}

    {#if isModel}
        <button type="button" class="add" on:click={onBack}>Voltar para a lista</button>
    {/if}

    <svelte:fragment slot="footer">
        <div class="footer">
            <div class="footer_summary">
                {items.length}
                {items.length === 1 ? 'item' : 'itens'} · {money(total)}
            </div>
            {#if canWrite}
                <button type="button" class="footer_save" disabled={show} on:click={onSave}>
                    {show ? 'Salvando…' : isModel ? 'Salvar modelo' : entity.quotationId ? 'Salvar alterações' : 'Salvar orçamento'}
                </button>
            {/if}
        </div>
    </svelte:fragment>
</Zsheet>

<style>
    /* valores base = tema escuro (padrão do app) */
    .list {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    /* item de pacote: recuado, embaixo do pacote a que pertence */
    .child {
        margin-left: 22px;
        opacity: 0.85;
    }
    .edit {
        padding: 10px;
        border-radius: 10px;
        background: var(--z-field-bg);
        border: 1px solid #2ecc7144;
    }
    .tag {
        display: inline-block;
        font-size: 11px;
        font-weight: 600;
        padding: 1px 7px;
        border-radius: 999px;
        background: var(--z-field-act-bg);
        margin-top: 3px;
    }
    .amount {
        flex: 0 0 auto;
        font-size: 14.5px;
        font-weight: 700;
        white-space: nowrap;
    }
    .add {
        border: 1px dashed #ffffff33;
        background: none;
        color: inherit;
        font: inherit;
        font-size: 14px;
        border-radius: 10px;
        padding: 10px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: var(--z-field-height);
    }
    .add.-solid {
        border-style: solid;
        border-color: var(--z-field-border);
        background: var(--z-field-bg);
    }

    .details,
    .actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .danger {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        width: 100%;
        text-align: left;
        background: none;
        border: 0px;
        color: #e58080;
        font: inherit;
        padding: 0px;
    }
    .danger_icon {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        height: 19px;
        font-size: 14px;
    }
    .danger_text {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }
    .danger_title {
        font-size: 14.5px;
        font-weight: 600;
        line-height: 19px;
    }
    .danger_hint {
        font-size: 12.5px;
        opacity: 0.55;
        line-height: 1.3;
        margin-top: 3px;
    }

    .footer {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .footer_summary {
        font-size: 12.5px;
        opacity: 0.7;
        text-align: center;
    }
    .footer_save {
        width: 100%;
        border: 0px;
        border-radius: 12px;
        background: #2ecc71;
        color: #10331f;
        font: inherit;
        font-size: 16px;
        font-weight: 700;
        padding: 14px;
    }
    .footer_save:disabled {
        opacity: 0.4;
    }

    :global(.theme_light) .add {
        border-color: #d8dbe0;
    }
    :global(.theme_light) .footer_save {
        color: #ffffff;
    }
    :global(.theme_light) .danger {
        color: #c0392b;
    }
</style>
